import type { TOC } from '@ember/component/template-only';
import { Heading, Text } from 'tio-ui/components/typography';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme options for the hero banner
 */
export type HeroBannerColor =
  | 'violet'
  | 'emerald'
  | 'amber'
  | 'blue'
  | 'teal'
  | 'red'
  | 'indigo'
  | 'gray';

/**
 * HeroBanner - A gradient hero/header section for page tops
 *
 * Provides consistent page headers with gradient backgrounds, icons,
 * titles, subtitles, and optional stat boxes or action areas.
 *
 * @example Basic usage
 * ```gts
 * <HeroBanner
 *   @icon={{PhPiggyBank}}
 *   @title="Section 127 Optimizer"
 *   @subtitle="Maximize your tax-free $5,250 education benefit"
 *   @color="emerald"
 * />
 * ```
 *
 * @example With stat box
 * ```gts
 * <HeroBanner
 *   @icon={{PhTrophy}}
 *   @title="Your Career Score"
 *   @subtitle="Track your progress"
 *   @color="violet"
 * >
 *   <:stat>
 *     <div class="text-4xl font-bold">720</div>
 *     <Text class="text-white/80">of 1,000 points</Text>
 *   </:stat>
 * </HeroBanner>
 * ```
 *
 * @example With actions
 * ```gts
 * <HeroBanner @title="Skills Gap Analysis" @color="red">
 *   <:actions>
 *     <Button>Take Action</Button>
 *   </:actions>
 * </HeroBanner>
 * ```
 */
interface HeroBannerSignature {
  Args: {
    /** Optional Phosphor icon component to display */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Subtitle/description text */
    subtitle?: string;
    /** Color theme for gradient */
    color?: HeroBannerColor;
    /** Heading level for accessibility (defaults to 1) */
    headingLevel?: 1 | 2 | 3;
  };
  Blocks: {
    /** Optional stat display on the right side */
    stat?: [];
    /** Optional action buttons */
    actions?: [];
    /** Optional custom content below title area */
    content?: [];
  };
  Element: HTMLDivElement;
}

const colorClasses: Record<HeroBannerColor, { gradient: string; subtitleText: string }> = {
  violet: {
    gradient: 'from-violet-600 to-purple-600',
    subtitleText: 'text-violet-100',
  },
  emerald: {
    gradient: 'from-emerald-600 to-teal-600',
    subtitleText: 'text-emerald-100',
  },
  amber: {
    gradient: 'from-amber-500 to-orange-500',
    subtitleText: 'text-amber-100',
  },
  blue: {
    gradient: 'from-blue-600 to-indigo-600',
    subtitleText: 'text-blue-100',
  },
  teal: {
    gradient: 'from-teal-600 to-cyan-600',
    subtitleText: 'text-teal-100',
  },
  red: {
    gradient: 'from-red-600 to-rose-500',
    subtitleText: 'text-red-100',
  },
  indigo: {
    gradient: 'from-indigo-600 to-violet-600',
    subtitleText: 'text-indigo-100',
  },
  gray: {
    gradient: 'from-gray-600 to-slate-600',
    subtitleText: 'text-gray-300',
  },
};

const getColorClasses = (color: HeroBannerColor = 'violet') => colorClasses[color];

const HeroBanner: TOC<HeroBannerSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <div
      class="bg-gradient-to-r {{colors.gradient}} rounded-xl p-8 text-white mb-6"
      role="banner"
      ...attributes
    >
      <div class="flex justify-between items-start">
        <div class="flex-1">
          <div class="flex items-center gap-2 mb-2">
            {{#if @icon}}
              {{#let @icon as |Icon|}}
                <Icon @size={{28}} />
              {{/let}}
            {{/if}}
            <Heading @level={{if @headingLevel @headingLevel 1}} class="text-white text-2xl">
              {{@title}}
            </Heading>
          </div>
          {{#if @subtitle}}
            <Text class="{{colors.subtitleText}} text-lg max-w-2xl">
              {{@subtitle}}
            </Text>
          {{/if}}
          {{#if (has-block "actions")}}
            <div class="mt-4 flex items-center gap-3">
              {{yield to="actions"}}
            </div>
          {{/if}}
        </div>

        {{#if (has-block "stat")}}
          <div class="text-right bg-white/10 rounded-lg p-4 backdrop-blur-sm">
            {{yield to="stat"}}
          </div>
        {{/if}}
      </div>

      {{#if (has-block "content")}}
        <div class="mt-6">
          {{yield to="content"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default HeroBanner;
